import {
  forwardRef,
  memo,
  useState,
  useRef,
  useEffect,
  useCallback
} from 'react';
import { classNames } from '../../utils/classNames';
import type { OptionsMenuProps } from './OptionsMenu.types';
import React from 'react';

const OptionsMenuComponent = forwardRef<HTMLDivElement, OptionsMenuProps>(
  (
    {
      options,
      onOptionClick,
      styleOverrides,
      className,
      triggerIcon,
      triggerStyleOverrides,
      ...restProps
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
          document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);

    const handleToggle = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setIsOpen(v => !v);
    }, []);

    const handleOptionClick = useCallback(
      (optionId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onOptionClick(optionId);
        setIsOpen(false);
      },
      [onOptionClick]
    );

    const defaultTriggerIcon = (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <circle cx="3" cy="8" r="1.5" />
        <circle cx="8" cy="8" r="1.5" />
        <circle cx="13" cy="8" r="1.5" />
      </svg>
    );

    return (
      <div
        ref={node => {
          // Apply both the forwarded ref and our internal ref
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
          if (menuRef && node) {
            // Using Object.assign to work around readonly restriction
            Object.assign(menuRef, { current: node });
          }
          // menuRef.current = node;
        }}
        className={classNames('options-menu', className)}
        style={styleOverrides}
        {...restProps}
      >
        <button
          type="button"
          className="options-menu__trigger"
          onClick={handleToggle}
          aria-label="Options menu"
          aria-expanded={isOpen}
          style={triggerStyleOverrides}
        >
          {triggerIcon || defaultTriggerIcon}
        </button>

        {isOpen && (
          <div className="options-menu__dropdown">
            {options.map(option => (
              <button
                key={option.id}
                type="button"
                className="options-menu__option"
                onClick={e => handleOptionClick(option.id, e)}
                disabled={option.disabled}
                style={option.style}
              >
                {option.icon && (
                  <span className="options-menu__option-icon">
                    {option.icon}
                  </span>
                )}
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

OptionsMenuComponent.displayName = 'OptionsMenu';

export const OptionsMenu = memo(OptionsMenuComponent);
